import { create } from 'zustand';

type ContextType = 'patient' | 'appointment' | 'lead' | 'plan';

/** Page-level context for the AI chatbot — tells the AI what the user is looking at */
export interface PageContext {
  /** Current route path e.g. "/packages" */
  route: string;
  /** Human-friendly page name e.g. "Packages" */
  pageName: string;
  /** Description of active filters e.g. "Status: Active, Date: Mar 1-27" */
  filters?: string;
  /** Summary of visible data e.g. "Showing 24 active packages" */
  dataSummary?: string;
  /** Any extra context the page wants to provide */
  extra?: string;
}

/** A single chat turn. Mirrors ChatMessage in ai-context.service.ts. */
export interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Everything the assistant has produced for one page/entity combination.
 *
 * Held here rather than in SidebarChat's local state for three reasons:
 *  - AISidebar mounts SidebarChat twice (desktop + mobile overlay); sharing
 *    state keeps them consistent and stops the pair from doing duplicate work
 *  - collapsing the sidebar unmounts the desktop chat, and we don't want
 *    re-expanding it to throw away a summary the user already paid for
 *  - navigating away and back reuses the summary instead of re-billing
 */
export interface AiChatSession {
  summary: string | null;
  messages: AiChatMessage[];
  /** True once the user explicitly ran the summary or asked a question. */
  started: boolean;
}

const EMPTY_SESSION: AiChatSession = { summary: null, messages: [], started: false };

/** Cap retained sessions so a long browsing session can't grow unbounded. */
const MAX_SESSIONS = 15;

/**
 * Identifies a chat session. Route + entity, so /patients with patient #5
 * focused is a different conversation from /patients with nothing focused.
 */
export function buildContextKey(
  route: string | null | undefined,
  contextType: ContextType | null | undefined,
  contextId: number | null | undefined,
): string {
  return `${route ?? ''}|${contextType ?? ''}:${contextId ?? ''}`;
}

export interface AISidebarState {
  isOpen: boolean;
  contextType: ContextType | null;
  contextId: number | null;
  /** Page-level context — always available regardless of entity context */
  pageContext: PageContext | null;
  /** Cached chat sessions keyed by buildContextKey() */
  sessions: Record<string, AiChatSession>;
  /** Insertion order of `sessions` keys, oldest first — used to trim */
  sessionOrder: string[];
  /** Opens sidebar AND sets entity context (used by "AI Insights" button) */
  openSidebar: (type: ContextType, id: number) => void;
  /** Collapses sidebar but keeps context (re-expand shows same data) */
  closeSidebar: () => void;
  /** Toggle open/close */
  toggle: () => void;
  /** Sets entity context without changing open state — used by detail pages on mount
   *  so the sidebar refreshes when navigating between records */
  setContext: (type: ContextType, id: number) => void;
  /** Sets page-level context (route, filters, data summary) — called by usePageContext hook */
  setPageContext: (ctx: PageContext) => void;
  /** Clears entity context (e.g. when navigating away from a detail page) */
  clearEntityContext: () => void;
  /** Marks a session as user-initiated — unblocks the entity context panels */
  startSession: (key: string) => void;
  /** Stores a generated page/entity summary */
  setSummary: (key: string, summary: string) => void;
  /** Appends chat turns to a session */
  appendMessages: (key: string, messages: AiChatMessage[]) => void;
}

/** Inserts/updates a session and trims the least-recently-created ones. */
function writeSession(
  state: AISidebarState,
  key: string,
  patch: Partial<AiChatSession>,
): Pick<AISidebarState, 'sessions' | 'sessionOrder'> {
  const existing = state.sessions[key] ?? EMPTY_SESSION;
  const sessions = { ...state.sessions, [key]: { ...existing, ...patch } };
  const sessionOrder = state.sessionOrder.includes(key)
    ? state.sessionOrder
    : [...state.sessionOrder, key];

  if (sessionOrder.length > MAX_SESSIONS) {
    const trimmed = sessionOrder.slice(sessionOrder.length - MAX_SESSIONS);
    const kept: Record<string, AiChatSession> = {};
    for (const k of trimmed) kept[k] = sessions[k];
    return { sessions: kept, sessionOrder: trimmed };
  }

  return { sessions, sessionOrder };
}

export const useAISidebarStore = create<AISidebarState>((set) => ({
  isOpen: false,
  contextType: null,
  contextId: null,
  pageContext: null,
  sessions: {},
  sessionOrder: [],
  openSidebar: (type, id) => set({ isOpen: true, contextType: type, contextId: id }),
  closeSidebar: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  setContext: (type, id) => set({ contextType: type, contextId: id }),
  setPageContext: (ctx) => set({ pageContext: ctx }),
  clearEntityContext: () => set({ contextType: null, contextId: null }),
  startSession: (key) => set((s) => writeSession(s, key, { started: true })),
  setSummary: (key, summary) => set((s) => writeSession(s, key, { summary, started: true })),
  appendMessages: (key, messages) =>
    set((s) =>
      writeSession(s, key, {
        started: true,
        messages: [...(s.sessions[key]?.messages ?? []), ...messages],
      }),
    ),
}));

/** Reads a session without creating one. Safe to call for unknown keys. */
export function selectSession(state: AISidebarState, key: string): AiChatSession {
  return state.sessions[key] ?? EMPTY_SESSION;
}
